import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ExternalLink,
  Globe,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { api, type CompPublicPageBlob, type CompPublicPageDraft } from '../lib/api';
import { Card } from './Card';
import { CopyLinkButton } from './CopyLinkButton';
import { FocalPointPicker } from './FocalPointPicker';
import { ImageDropzone } from './ImageDropzone';
import { SaveBar } from './SaveBar';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';

// ── Local row types ────────────────────────────────────────────────────────────

type ScheduleRow = { label: string; date: string };
type PrizeRow    = { place: string; prize: string };
type SponsorRow  = { name: string; url: string; logoAssetUrl?: string; logoObjectPosition?: string };

interface FormState {
  published: boolean;
  heroLogoAssetUrl?: string;
  heroLogoObjectPosition?: string;
  heroBannerAssetUrl?: string;
  heroBannerObjectPosition?: string;
  seasonTag: string;
  title: string;
  subtitle: string;
  game: string;
  prizePool: string;
  about: string;
  signupOpensAt: string;
  signupClosesAt: string;
  startsAt: string;
  schedule: ScheduleRow[];
  modes: string[];
  prizes: PrizeRow[];
  rules: string;
  sponsors: SponsorRow[];
  contactEmail: string;
  contactDiscordInviteUrl: string;
  contactNote: string;
  registerCtaText: string;
}

const EMPTY: FormState = {
  published: false,
  seasonTag: '',
  title: '',
  subtitle: '',
  game: '',
  prizePool: '',
  about: '',
  signupOpensAt: '',
  signupClosesAt: '',
  startsAt: '',
  schedule: [],
  modes: [],
  prizes: [],
  rules: '',
  sponsors: [],
  contactEmail: '',
  contactDiscordInviteUrl: '',
  contactNote: '',
  registerCtaText: '',
};

// ── Blob ↔ form conversion ─────────────────────────────────────────────────────

function fromBlob(draft: CompPublicPageDraft): FormState {
  return {
    published:              draft.published,
    heroLogoAssetUrl:       draft.hero?.logoAssetUrl,
    heroLogoObjectPosition: draft.hero?.logoObjectPosition,
    heroBannerAssetUrl:     draft.hero?.bannerAssetUrl,
    heroBannerObjectPosition: draft.hero?.bannerObjectPosition,
    seasonTag:              draft.hero?.seasonTag ?? '',
    title:                  draft.hero?.title ?? '',
    subtitle:               draft.hero?.subtitle ?? '',
    game:                   draft.game ?? '',
    prizePool:              draft.prizePool ?? '',
    about:                  draft.about ?? '',
    signupOpensAt:          (draft.keyDates?.signupOpensAt as string | undefined) ?? '',
    signupClosesAt:         (draft.keyDates?.signupClosesAt as string | undefined) ?? '',
    startsAt:               (draft.keyDates?.startsAt as string | undefined) ?? '',
    schedule:               (draft.schedule ?? []).map(r => ({ label: r.label, date: r.date })),
    modes:                  (draft.modes ?? []).slice(),
    prizes:                 (draft.prizes ?? []).map(r => ({ place: r.place, prize: r.prize })),
    rules:                  draft.rules ?? '',
    sponsors:               (draft.sponsors ?? []).map(s => ({
      name:         s.name,
      url:          s.url ?? '',
      logoAssetUrl: s.logoAssetUrl,
      logoObjectPosition: s.logoObjectPosition,
    })),
    contactEmail:           draft.contact?.email ?? '',
    contactDiscordInviteUrl: draft.contact?.discordInviteUrl ?? '',
    contactNote:            draft.contact?.note ?? '',
    registerCtaText:        draft.registerCtaText ?? '',
  };
}

function toBlob(form: FormState): CompPublicPageBlob & { game?: string; prizePool?: string } {
  // Hero — include only if any sub-field is set
  const heroFields: CompPublicPageBlob['hero'] = {};
  if (form.heroLogoAssetUrl)      heroFields.logoAssetUrl   = form.heroLogoAssetUrl;
  if (form.heroLogoAssetUrl && form.heroLogoObjectPosition)     heroFields.logoObjectPosition   = form.heroLogoObjectPosition;
  if (form.heroBannerAssetUrl)    heroFields.bannerAssetUrl = form.heroBannerAssetUrl;
  if (form.heroBannerAssetUrl && form.heroBannerObjectPosition) heroFields.bannerObjectPosition = form.heroBannerObjectPosition;
  if (form.seasonTag.trim())      heroFields.seasonTag      = form.seasonTag.trim();
  if (form.title.trim())          heroFields.title          = form.title.trim();
  if (form.subtitle.trim())       heroFields.subtitle       = form.subtitle.trim();
  const hero = Object.keys(heroFields).length > 0 ? heroFields : undefined;

  // About
  const about = form.about.trim() || undefined;

  // Key dates — only the three fixed keys the normalizer keeps
  const kd: Record<string, string> = {};
  if (form.signupOpensAt.trim())  kd.signupOpensAt  = form.signupOpensAt.trim();
  if (form.signupClosesAt.trim()) kd.signupClosesAt = form.signupClosesAt.trim();
  if (form.startsAt.trim())       kd.startsAt       = form.startsAt.trim();
  const keyDates = Object.keys(kd).length > 0 ? kd : undefined;

  // Schedule — row needs both label + date
  const validSchedule = form.schedule.filter(r => r.label.trim() && r.date.trim());
  const schedule = validSchedule.length > 0
    ? validSchedule.map(r => ({ label: r.label.trim(), date: r.date.trim() }))
    : undefined;

  // Modes
  const validModes = form.modes.map(m => m.trim()).filter(Boolean);
  const modes = validModes.length > 0 ? validModes : undefined;

  // Prizes — row needs both place + prize
  const validPrizes = form.prizes.filter(r => r.place.trim() && r.prize.trim());
  const prizes = validPrizes.length > 0
    ? validPrizes.map(r => ({ place: r.place.trim(), prize: r.prize.trim() }))
    : undefined;

  // Rules
  const rules = form.rules.trim() || undefined;

  // Sponsors — name is required
  const validSponsors = form.sponsors.filter(s => s.name.trim());
  const sponsors = validSponsors.length > 0
    ? validSponsors.map(s => ({
        name: s.name.trim(),
        ...(s.url.trim() ? { url: s.url.trim() } : {}),
        ...(s.logoAssetUrl ? { logoAssetUrl: s.logoAssetUrl } : {}),
        ...(s.logoAssetUrl && s.logoObjectPosition ? { logoObjectPosition: s.logoObjectPosition } : {}),
      }))
    : undefined;

  // Contact
  const contactFields: CompPublicPageBlob['contact'] = {};
  if (form.contactEmail.trim())            contactFields.email            = form.contactEmail.trim();
  if (form.contactDiscordInviteUrl.trim()) contactFields.discordInviteUrl = form.contactDiscordInviteUrl.trim();
  if (form.contactNote.trim())             contactFields.note             = form.contactNote.trim();
  const contact = Object.keys(contactFields).length > 0 ? contactFields : undefined;

  // CTA
  const registerCtaText = form.registerCtaText.trim() || undefined;

  // Top-level comp fields
  const game      = form.game.trim() || undefined;
  const prizePool = form.prizePool.trim() || undefined;

  return {
    published: form.published,
    ...(hero            ? { hero }            : {}),
    ...(about           ? { about }           : {}),
    ...(keyDates        ? { keyDates }        : {}),
    ...(schedule        ? { schedule }        : {}),
    ...(modes           ? { modes }           : {}),
    ...(prizes          ? { prizes }          : {}),
    ...(rules           ? { rules }           : {}),
    ...(sponsors        ? { sponsors }        : {}),
    ...(contact         ? { contact }         : {}),
    ...(registerCtaText ? { registerCtaText } : {}),
    ...(game            ? { game }            : {}),
    ...(prizePool       ? { prizePool }       : {}),
  };
}

// ── Small layout helpers ───────────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <h3 className="mb-3 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
      {label}
    </h3>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-500">
      {children}
    </p>
  );
}

function AddRowButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2 flex items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200"
    >
      <Plus className="h-3 w-3" strokeWidth={2.5} />
      {label}
    </button>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-8 w-8 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-red-700 hover:text-red-400"
      aria-label="Remove row"
    >
      <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
    </button>
  );
}

// ── Image upload control ───────────────────────────────────────────────────────

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

function ImageUploadControl({
  compId,
  currentUrl,
  label,
  onUploaded,
  onClear,
}: {
  compId: string;
  currentUrl?: string;
  label: string;
  onUploaded: (publicUrl: string) => void;
  onClear: () => void;
}) {
  const inputRef  = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Unsupported type. Use PNG, JPEG, WebP, or SVG.');
      return;
    }
    if (file.size === 0 || file.size > MAX_FILE_BYTES) {
      setError('File must be 1 byte – 5 MB.');
      return;
    }

    setLoading(true);
    try {
      const { uploadUrl, publicUrl } = await api.compPublicPageUploadUrl(compId, file.type, file.size);
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      onUploaded(publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-1.5">
      <FieldLabel>{label}</FieldLabel>

      {currentUrl ? (
        <div className="flex items-start gap-2">
          <img
            src={currentUrl}
            alt={label}
            className="h-14 w-auto max-w-[10rem] border border-iron-800 object-contain bg-iron-950"
          />
          <button
            type="button"
            onClick={onClear}
            className="flex h-7 w-7 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-red-700 hover:text-red-400"
            aria-label={`Remove ${label}`}
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      ) : (
        <p className="text-[10px] italic text-iron-500">No image set.</p>
      )}

      {/* Wrap mode — drop anywhere on the button hands the file to the same
          handleFile the click path uses; no new upload logic. */}
      <ImageDropzone onFile={file => void handleFile(file)} accept={ALLOWED_IMAGE_TYPES.join(',')} disabled={loading}>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) { void handleFile(file); }
              e.target.value = '';
            }}
          />
          <button
            type="button"
            disabled={loading}
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1.5 border border-dashed border-iron-800 px-3 py-1.5 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
              : <Upload className="h-3 w-3" strokeWidth={2.5} />
            }
            {loading ? 'Uploading…' : (currentUrl ? 'Replace or drag a new image here' : 'Upload or drag an image here')}
          </button>
        </div>
      </ImageDropzone>

      {error && (
        <div className="flex items-center gap-1.5 text-[10px] text-red-400">
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          {error}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function CompPublicPageEditor({ compId }: { compId: string }) {
  const qc = useQueryClient();

  // ── ALL hooks before any early return ────────────────────────────────────────
  const [form, setForm]   = useState<FormState>(EMPTY);
  // Snapshot of the last-loaded/last-saved form — the dirty-tracking baseline
  // the sticky SaveBar reads, and the Discard target. No parallel state
  // invented beyond this one snapshot.
  const [savedSnapshot, setSavedSnapshot] = useState<FormState>(EMPTY);
  const [saved, setSaved] = useState(false);

  const { data: draft, isLoading: draftLoading } = useQuery({
    queryKey: ['compPublicPage', compId],
    queryFn: () => api.getCompPublicPage(compId),
  });

  // Initialise form from loaded draft (fires once when data arrives) — also
  // resets the dirty-tracking snapshot so a post-save refetch never leaves
  // the form looking falsely dirty.
  useEffect(() => {
    if (!draft) return;
    const next = fromBlob(draft);
    setForm(next);
    setSavedSnapshot(next);
  }, [draft]);

  const saveMut = useMutation({
    mutationFn: (blob: CompPublicPageBlob & { game?: string; prizePool?: string }) =>
      api.putCompPublicPage(compId, blob),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      qc.invalidateQueries({ queryKey: ['compPublicPage', compId] });
    },
  });

  const dirty = JSON.stringify(form) !== JSON.stringify(savedSnapshot);
  // ── End of hooks zone ─────────────────────────────────────────────────────────

  if (draftLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 py-6 text-iron-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading public page settings…</span>
        </div>
      </Card>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────────
  const publicPath = `/comp/${encodeURIComponent(compId)}`;
  const publicUrl  = `${window.location.origin}${publicPath}`;

  // ── Form helpers ──────────────────────────────────────────────────────────────

  function patch(update: Partial<FormState>) {
    setForm(prev => ({ ...prev, ...update }));
  }

  function updateSchedule(idx: number, update: Partial<ScheduleRow>) {
    patch({ schedule: form.schedule.map((r, i) => i === idx ? { ...r, ...update } : r) });
  }

  function updatePrize(idx: number, update: Partial<PrizeRow>) {
    patch({ prizes: form.prizes.map((r, i) => i === idx ? { ...r, ...update } : r) });
  }

  function updateSponsor(idx: number, update: Partial<SponsorRow>) {
    patch({ sponsors: form.sponsors.map((r, i) => i === idx ? { ...r, ...update } : r) });
  }

  function updateMode(idx: number, value: string) {
    patch({ modes: form.modes.map((m, i) => i === idx ? value : m) });
  }

  function removeAt<T>(arr: T[], idx: number): T[] {
    return arr.filter((_, i) => i !== idx);
  }

  const handleSave = () => {
    setSaved(false);
    const snapshot = form;
    saveMut.mutate(toBlob(snapshot), {
      onSuccess: () => setSavedSnapshot(snapshot),
    });
  };

  // ── JSX ───────────────────────────────────────────────────────────────────────

  return (
    <div className={`${SETTLE} space-y-4`} style={settleDelay(0)}>

      {/* ── Discoverability card ── */}
      <Card>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
            <Globe className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
            Public Competition Page
          </h2>

          {/* Publish toggle — local state; persisted on Save */}
          <button
            type="button"
            onClick={() => patch({ published: !form.published })}
            className={cn(
              'flex items-center gap-2 border px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors',
              form.published
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                : 'border-iron-700 bg-iron-950 text-iron-400 hover:border-iron-600 hover:text-iron-200',
            )}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                form.published ? 'bg-emerald-400' : 'bg-iron-600',
              )}
            />
            {form.published ? 'Published — live' : 'Draft — not public'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <a
            href={publicPath}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500 hover:text-forge-200"
          >
            <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
            View public page
          </a>
          <CopyLinkButton
            url={publicUrl}
            label="Copy link"
            className="flex items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500 hover:text-forge-200"
          />
          {!form.published && (
            <span className="text-[10px] text-iron-500">
              Page exists but won't be visible to others until published.
            </span>
          )}
        </div>
      </Card>

      {/* ── Hero ── */}
      <Card>
        <SectionHeader label="Hero" />
        <div className="space-y-4">
          <ImageUploadControl
            compId={compId}
            label="Logo"
            currentUrl={form.heroLogoAssetUrl}
            onUploaded={url => patch({ heroLogoAssetUrl: url, heroLogoObjectPosition: undefined })}
            onClear={() => patch({ heroLogoAssetUrl: undefined, heroLogoObjectPosition: undefined })}
          />
          {form.heroLogoAssetUrl && (
            <FocalPointPicker
              src={form.heroLogoAssetUrl}
              aspect={1}
              value={form.heroLogoObjectPosition}
              onChange={pos => patch({ heroLogoObjectPosition: pos })}
              label="Logo focal point"
            />
          )}
          <ImageUploadControl
            compId={compId}
            label="Banner"
            currentUrl={form.heroBannerAssetUrl}
            onUploaded={url => patch({ heroBannerAssetUrl: url, heroBannerObjectPosition: undefined })}
            onClear={() => patch({ heroBannerAssetUrl: undefined, heroBannerObjectPosition: undefined })}
          />
          {form.heroBannerAssetUrl && (
            <FocalPointPicker
              src={form.heroBannerAssetUrl}
              aspect={3.2}
              value={form.heroBannerObjectPosition}
              onChange={pos => patch({ heroBannerObjectPosition: pos })}
              label="Banner focal point"
            />
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <FieldLabel>Season tag</FieldLabel>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="e.g. Season 4"
                maxLength={100}
                value={form.seasonTag}
                onChange={e => patch({ seasonTag: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel>Title</FieldLabel>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="e.g. CDL Open 2026"
                maxLength={200}
                value={form.title}
                onChange={e => patch({ title: e.target.value })}
              />
            </div>
          </div>
          <div>
            <FieldLabel>Subtitle</FieldLabel>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="Short supporting line shown below the title"
              maxLength={200}
              value={form.subtitle}
              onChange={e => patch({ subtitle: e.target.value })}
            />
          </div>
        </div>
      </Card>

      {/* ── Competition basics ── */}
      <Card>
        <SectionHeader label="Competition basics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel>Game</FieldLabel>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="e.g. Call of Duty: Black Ops 7"
              maxLength={200}
              value={form.game}
              onChange={e => patch({ game: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Prize pool</FieldLabel>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="e.g. R50,000"
              maxLength={200}
              value={form.prizePool}
              onChange={e => patch({ prizePool: e.target.value })}
            />
          </div>
        </div>
      </Card>

      {/* ── About ── */}
      <Card>
        <SectionHeader label="About" />
        <FieldLabel>Description</FieldLabel>
        <textarea
          className="input w-full resize-y text-sm"
          rows={5}
          placeholder="Describe this competition — format, purpose, who can enter…&#10;&#10;Blank lines become paragraph breaks on the public page."
          maxLength={3000}
          value={form.about}
          onChange={e => patch({ about: e.target.value })}
        />
        <p className="mt-1 text-[10px] text-iron-500">
          Blank lines split into paragraphs. Max 3 000 characters.
        </p>
      </Card>

      {/* ── Key dates ── */}
      <Card>
        <SectionHeader label="Key dates" />
        <p className="mb-3 text-[10px] text-iron-500">
          Free-text — enter whatever format suits (e.g. "August 2026", "1 Aug 2026").
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <FieldLabel>Sign-up opens</FieldLabel>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="e.g. 1 July 2026"
              maxLength={50}
              value={form.signupOpensAt}
              onChange={e => patch({ signupOpensAt: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Sign-up closes</FieldLabel>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="e.g. 31 July 2026"
              maxLength={50}
              value={form.signupClosesAt}
              onChange={e => patch({ signupClosesAt: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Starts</FieldLabel>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="e.g. August 2026"
              maxLength={50}
              value={form.startsAt}
              onChange={e => patch({ startsAt: e.target.value })}
            />
          </div>
        </div>
      </Card>

      {/* ── Schedule ── */}
      <Card>
        <SectionHeader label="Schedule" />
        <div className="space-y-2">
          {form.schedule.map((r, i) => (
            <div
              key={i}
              className="flex items-center gap-2 border border-iron-800/60 bg-iron-950 p-2"
            >
              <input
                type="text"
                className="input flex-1 text-sm"
                placeholder="Label (e.g. Group Stage)"
                maxLength={100}
                value={r.label}
                onChange={e => updateSchedule(i, { label: e.target.value })}
              />
              <input
                type="text"
                className="input flex-1 text-sm"
                placeholder="Date (e.g. 5–12 Aug)"
                maxLength={50}
                value={r.date}
                onChange={e => updateSchedule(i, { date: e.target.value })}
              />
              <RemoveButton onClick={() => patch({ schedule: removeAt(form.schedule, i) })} />
            </div>
          ))}
          {form.schedule.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No schedule rows yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add schedule row"
          onClick={() => patch({ schedule: [...form.schedule, { label: '', date: '' }] })}
        />
      </Card>

      {/* ── Game modes ── */}
      <Card>
        <SectionHeader label="Game modes" />
        <div className="space-y-2">
          {form.modes.map((m, i) => (
            <div
              key={i}
              className="flex items-center gap-2 border border-iron-800/60 bg-iron-950 p-2"
            >
              {/* min-w-0 lets the input shrink inside the flex row — without
                  it the browser's default input min-width forces the row (and
                  the whole page) into horizontal overflow on mobile. */}
              <input
                type="text"
                className="input min-w-0 flex-1 text-sm"
                placeholder="e.g. Search & Destroy"
                maxLength={100}
                value={m}
                onChange={e => updateMode(i, e.target.value)}
              />
              <RemoveButton onClick={() => patch({ modes: removeAt(form.modes, i) })} />
            </div>
          ))}
          {form.modes.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No game modes listed yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add mode"
          onClick={() => patch({ modes: [...form.modes, ''] })}
        />
      </Card>

      {/* ── Prizes ── */}
      <Card>
        <SectionHeader label="Prizes" />
        <div className="space-y-2">
          {form.prizes.map((r, i) => (
            <div
              key={i}
              className="flex flex-wrap items-center gap-2 border border-iron-800/60 bg-iron-950 p-2"
            >
              {/* flex-wrap on the row + per-input min widths: on phone widths
                  the pair stacks instead of shoving the page into sideways
                  scroll (Lisa's mobile screenshots, 2026-07-13). */}
              <input
                type="text"
                className="input min-w-[6rem] flex-1 text-sm"
                placeholder="Place (e.g. 1st)"
                maxLength={50}
                value={r.place}
                onChange={e => updatePrize(i, { place: e.target.value })}
              />
              <input
                type="text"
                className="input min-w-[9rem] flex-[2] text-sm"
                placeholder="Prize (e.g. R20,000)"
                maxLength={200}
                value={r.prize}
                onChange={e => updatePrize(i, { prize: e.target.value })}
              />
              <RemoveButton onClick={() => patch({ prizes: removeAt(form.prizes, i) })} />
            </div>
          ))}
          {form.prizes.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No prize rows yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add prize"
          onClick={() => patch({ prizes: [...form.prizes, { place: '', prize: '' }] })}
        />
      </Card>

      {/* ── Rules ── */}
      <Card>
        <SectionHeader label="Rules" />
        <FieldLabel>Rulebook / competition rules</FieldLabel>
        <textarea
          className="input w-full resize-y text-sm"
          rows={6}
          placeholder="Eligibility, format, conduct rules…"
          maxLength={5000}
          value={form.rules}
          onChange={e => patch({ rules: e.target.value })}
        />
        <p className="mt-1 text-[10px] text-iron-500">Max 5 000 characters.</p>
      </Card>

      {/* ── Sponsors ── */}
      <Card>
        <SectionHeader label="Sponsors" />
        <div className="space-y-4">
          {form.sponsors.map((s, i) => (
            <div
              key={i}
              className="space-y-2 border border-iron-800/60 bg-iron-950 p-3"
            >
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="input flex-1 text-sm"
                  placeholder="Sponsor name (required)"
                  maxLength={100}
                  value={s.name}
                  onChange={e => updateSponsor(i, { name: e.target.value })}
                />
                <input
                  type="text"
                  className="input flex-1 text-sm"
                  placeholder="Website URL (optional)"
                  value={s.url}
                  onChange={e => updateSponsor(i, { url: e.target.value })}
                />
                <RemoveButton onClick={() => patch({ sponsors: removeAt(form.sponsors, i) })} />
              </div>
              <ImageUploadControl
                compId={compId}
                label="Logo"
                currentUrl={s.logoAssetUrl}
                onUploaded={url => updateSponsor(i, { logoAssetUrl: url, logoObjectPosition: undefined })}
                onClear={() => updateSponsor(i, { logoAssetUrl: undefined, logoObjectPosition: undefined })}
              />
              {s.logoAssetUrl && (
                <FocalPointPicker
                  src={s.logoAssetUrl}
                  aspect={1}
                  value={s.logoObjectPosition}
                  onChange={pos => updateSponsor(i, { logoObjectPosition: pos })}
                  label="Logo focal point"
                />
              )}
            </div>
          ))}
          {form.sponsors.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No sponsors listed yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add sponsor"
          onClick={() => patch({ sponsors: [...form.sponsors, { name: '', url: '' }] })}
        />
      </Card>

      {/* ── Contact ── */}
      <Card>
        <SectionHeader label="Contact" />
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>Email</FieldLabel>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="contact@yourorg.gg"
                maxLength={254}
                value={form.contactEmail}
                onChange={e => patch({ contactEmail: e.target.value })}
              />
            </div>
            <div>
              <FieldLabel>Discord invite URL</FieldLabel>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="https://discord.gg/…"
                value={form.contactDiscordInviteUrl}
                onChange={e => patch({ contactDiscordInviteUrl: e.target.value })}
              />
            </div>
          </div>
          <div>
            <FieldLabel>Note</FieldLabel>
            <textarea
              className="input w-full resize-y text-sm"
              rows={2}
              placeholder="Anything else participants should know about reaching you…"
              maxLength={2000}
              value={form.contactNote}
              onChange={e => patch({ contactNote: e.target.value })}
            />
          </div>
        </div>
      </Card>

      {/* ── Call to action ── */}
      <Card>
        <SectionHeader label="Call to action" />
        <FieldLabel>Register button text</FieldLabel>
        <input
          type="text"
          className="input w-full text-sm"
          placeholder="e.g. Register your team"
          maxLength={100}
          value={form.registerCtaText}
          onChange={e => patch({ registerCtaText: e.target.value })}
        />
        <p className="mt-1 text-[10px] text-iron-500">
          Overrides the default "Sign up" text on the public page's CTA button.
        </p>
      </Card>

      {/* ── Save bar ── */}
      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        saved={saved}
        error={saveMut.isError ? (saveMut.error as Error).message : null}
        onSave={handleSave}
        onDiscard={() => setForm(savedSnapshot)}
      />
    </div>
  );
}
